/**
 * Invoice Type Codes according to UN/CEFACT Code List 1001
 * Source: UNTDID 1001 - Document type code
 * Used in XRechnung, ZUGFeRD, and other electronic invoice formats
 */

export interface InvoiceTypeInfo {
  code: string;              // Official invoice type code
  name: string;              // English name
  germanName: string;        // German name
  description: string;       // Detailed description
  category: string;          // Category (invoice, credit, etc.)
  isCommon: boolean;         // Commonly used in EU invoicing
  isActive: boolean;         // Currently active
  requiresReference: boolean; // Requires reference to original document
  examples: string[];        // Example use cases
}

export const INVOICE_TYPE_DATA: Record<string, InvoiceTypeInfo> = {
  // Standard Invoice Types
  '380': {
    code: '380',
    name: 'Commercial invoice',
    germanName: 'Handelsrechnung',
    description: 'Standard commercial invoice for goods or services',
    category: 'Invoice',
    isCommon: true,
    isActive: true,
    requiresReference: false,
    examples: ['Standard invoice', 'Sales invoice', 'Service invoice', 'B2B invoice']
  },
  
  // Credit and Debit Notes
  '381': {
    code: '381',
    name: 'Credit note',
    germanName: 'Gutschrift',
    description: 'Credit note to reduce the amount of a previous invoice',
    category: 'Credit Note',
    isCommon: true,
    isActive: true,
    requiresReference: true,
    examples: ['Credit memo', 'Refund note', 'Return credit', 'Discount credit']
  },
  
  '383': {
    code: '383',
    name: 'Debit note',
    germanName: 'Belastungsanzeige',
    description: 'Debit note to increase the amount of a previous invoice',
    category: 'Debit Note',
    isCommon: true,
    isActive: true,
    requiresReference: true,
    examples: ['Additional charges', 'Surcharge note', 'Penalty charge', 'Additional costs']
  },
  
  // Corrected Invoice
  '384': {
    code: '384',
    name: 'Corrected invoice',
    germanName: 'Korrigierte Rechnung',
    description: 'Corrected version of a previously sent invoice',
    category: 'Corrected Invoice',
    isCommon: true,
    isActive: true,
    requiresReference: true,
    examples: ['Invoice correction', 'Amended invoice', 'Revised invoice', 'Updated invoice']
  },
  
  // Self-Billed Invoice
  '389': {
    code: '389',
    name: 'Self-billed invoice',
    germanName: 'Selbstfakturierte Rechnung',
    description: 'Invoice created by the buyer instead of the seller',
    category: 'Self-Billed',
    isCommon: true,
    isActive: true,
    requiresReference: false,
    examples: ['Buyer-created invoice', 'Self-billing', 'Reverse invoice', 'Customer invoice']
  },
  
  // Prepayment Invoice
  '386': {
    code: '386',
    name: 'Prepayment invoice',
    germanName: 'Vorauszahlungsrechnung',
    description: 'Invoice for prepayment or advance payment',
    category: 'Prepayment',
    isCommon: true,
    isActive: true,
    requiresReference: false,
    examples: ['Advance payment', 'Down payment', 'Prepayment request', 'Deposit invoice']
  },
  
  // Final Invoice
  '387': {
    code: '387',
    name: 'Final invoice',
    germanName: 'Schlussrechnung',
    description: 'Final invoice after prepayments have been made',
    category: 'Final Invoice',
    isCommon: true,
    isActive: true,
    requiresReference: true,
    examples: ['Final payment', 'Balance invoice', 'Settlement invoice', 'Completion invoice']
  },
  
  // Proforma Invoice
  '325': {
    code: '325',
    name: 'Proforma invoice',
    germanName: 'Proformarechnung',
    description: 'Preliminary invoice for information purposes',
    category: 'Proforma',
    isCommon: true,
    isActive: true,
    requiresReference: false,
    examples: ['Quote invoice', 'Preliminary invoice', 'Customs invoice', 'Estimate invoice']
  },
  
  // Accounting Purpose Invoice
  '751': {
    code: '751',
    name: 'Invoice information for accounting purposes',
    germanName: 'Rechnungsinformationen für Buchhaltungszwecke',
    description: 'Invoice created for accounting and bookkeeping purposes',
    category: 'Accounting',
    isCommon: true,
    isActive: true,
    requiresReference: false,
    examples: ['Accounting invoice', 'Bookkeeping document', 'Internal invoice', 'Accounting record']
  },
  
  // Partial Invoice
  '326': {
    code: '326',
    name: 'Partial invoice',
    germanName: 'Teilrechnung',
    description: 'Invoice for partial delivery or service',
    category: 'Partial Invoice',
    isCommon: true,
    isActive: true,
    requiresReference: false,
    examples: ['Progress invoice', 'Milestone invoice', 'Partial delivery', 'Installment invoice']
  },
  
  // Substitute Invoice
  '395': {
    code: '395',
    name: 'Substitute invoice',
    germanName: 'Ersatzrechnung',
    description: 'Invoice that replaces a previously sent invoice',
    category: 'Substitute',
    isCommon: false,
    isActive: true,
    requiresReference: true,
    examples: ['Replacement invoice', 'Substitute document', 'Alternative invoice']
  },
  
  // Consignment Invoice
  '82': {
    code: '82',
    name: 'Consignment invoice',
    germanName: 'Konsignationsrechnung',
    description: 'Invoice for consignment goods',
    category: 'Consignment',
    isCommon: false,
    isActive: true,
    requiresReference: false,
    examples: ['Consignment billing', 'Consignment settlement', 'Consignment charges']
  },
  
  // Freight Invoice
  '700': {
    code: '700',
    name: 'Freight invoice',
    germanName: 'Frachtrechnung',
    description: 'Invoice for freight and transportation services',
    category: 'Freight',
    isCommon: false,
    isActive: true,
    requiresReference: false,
    examples: ['Shipping invoice', 'Transportation bill', 'Freight charges', 'Logistics invoice']
  },
  
  // Insurance Invoice
  '393': {
    code: '393',
    name: 'Insurance invoice',
    germanName: 'Versicherungsrechnung',
    description: 'Invoice for insurance services',
    category: 'Insurance',
    isCommon: false,
    isActive: true,
    requiresReference: false,
    examples: ['Insurance premium', 'Insurance billing', 'Policy invoice']
  },
  
  // Customs Invoice
  '935': {
    code: '935',
    name: 'Customs invoice',
    germanName: 'Zollrechnung',
    description: 'Invoice for customs and duty purposes',
    category: 'Customs',
    isCommon: false,
    isActive: true,
    requiresReference: false,
    examples: ['Customs declaration', 'Duty invoice', 'Import invoice', 'Export invoice']
  }
};

// Common invoice types for prioritized display
export const COMMON_INVOICE_TYPES = ['380', '381', '384', '389', '386', '387', '325', '751'] as const;

// Invoice type categories for organized display
export const INVOICE_TYPE_CATEGORIES = {
  'Standard': ['380', '326'],
  'Credit & Debit': ['381', '383'],
  'Corrections': ['384', '395'],
  'Prepayment': ['386', '387'],
  'Proforma': ['325'],
  'Special': ['389', '751'],
  'Industry Specific': ['82', '700', '393', '935']
} as const;

// Get all active invoice type codes
export const getAllInvoiceTypeCodes = (): string[] => {
  return Object.keys(INVOICE_TYPE_DATA).filter(code => INVOICE_TYPE_DATA[code].isActive);
};

// Get invoice type info by code
export const getInvoiceTypeInfo = (code: string): InvoiceTypeInfo | undefined => {
  return INVOICE_TYPE_DATA[code];
};

// Get invoice type name by code
export const getInvoiceTypeName = (code: string): string => {
  const type = getInvoiceTypeInfo(code);
  return type?.name || code;
};

// Get German invoice type name by code
export const getInvoiceTypeGermanName = (code: string): string => {
  const type = getInvoiceTypeInfo(code);
  return type?.germanName || code;
};

// Get formatted invoice type display (code + name)
export const getFormattedInvoiceTypeDisplay = (code: string): string => {
  const type = getInvoiceTypeInfo(code);
  return type ? `${code} - ${type.name}` : code;
};

// Get formatted invoice type display with German name
export const getFormattedInvoiceTypeDisplayGerman = (code: string): string => {
  const type = getInvoiceTypeInfo(code);
  return type ? `${code} - ${type.germanName}` : code;
};

// Check if invoice type requires reference to original document
export const requiresOriginalReference = (code: string): boolean => {
  const type = getInvoiceTypeInfo(code);
  return type?.requiresReference || false;
};

// Get invoice types by category
export const getInvoiceTypesByCategory = (category: keyof typeof INVOICE_TYPE_CATEGORIES): InvoiceTypeInfo[] => {
  const typeCodes = INVOICE_TYPE_CATEGORIES[category];
  return typeCodes.map(code => INVOICE_TYPE_DATA[code]).filter(Boolean);
};

// Search invoice types by name, German name, or code
export const searchInvoiceTypes = (query: string): InvoiceTypeInfo[] => {
  const searchTerm = query.toLowerCase();
  return Object.values(INVOICE_TYPE_DATA)
    .filter(type => 
      type.isActive && (
        type.name.toLowerCase().includes(searchTerm) ||
        type.germanName.toLowerCase().includes(searchTerm) ||
        type.code.toLowerCase().includes(searchTerm) ||
        type.description.toLowerCase().includes(searchTerm)
      )
    )
    .sort((a, b) => {
      // Prioritize exact matches
      const aExact = a.code.toLowerCase() === searchTerm;
      const bExact = b.code.toLowerCase() === searchTerm;
      
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;
      
      // Then prioritize common types
      const aCommon = a.isCommon;
      const bCommon = b.isCommon;
      
      if (aCommon && !bCommon) return -1;
      if (!aCommon && bCommon) return 1;
      
      // Finally alphabetical
      return a.name.localeCompare(b.name);
    });
};

// Get suggested invoice types for specific contexts
export const getSuggestedInvoiceTypes = (context: 'standard' | 'correction' | 'prepayment' | 'all' = 'standard'): string[] => {
  switch (context) {
    case 'standard':
      return ['380', '326'];
    case 'correction':
      return ['381', '383', '384', '395'];
    case 'prepayment':
      return ['386', '387'];
    case 'all':
      return COMMON_INVOICE_TYPES.slice();
    default:
      return ['380'];
  }
};

// Validate invoice type code
export const isValidInvoiceType = (code: string): boolean => {
  return INVOICE_TYPE_DATA.hasOwnProperty(code) && INVOICE_TYPE_DATA[code].isActive;
};

// Get invoice types that are credit/debit notes
export const getCreditDebitTypes = (): string[] => {
  return Object.keys(INVOICE_TYPE_DATA).filter(code => 
    INVOICE_TYPE_DATA[code].category === 'Credit Note' || 
    INVOICE_TYPE_DATA[code].category === 'Debit Note'
  );
};

// Get invoice types that require original document reference
export const getTypesRequiringReference = (): string[] => {
  return Object.keys(INVOICE_TYPE_DATA).filter(code => 
    INVOICE_TYPE_DATA[code].requiresReference
  );
};

// Check if invoice type is a credit note
export const isCreditNote = (code: string): boolean => {
  const type = getInvoiceTypeInfo(code);
  return type?.category === 'Credit Note';
};

// Check if invoice type is a debit note
export const isDebitNote = (code: string): boolean => {
  const type = getInvoiceTypeInfo(code);
  return type?.category === 'Debit Note';
};

// Check if invoice type is a correction
export const isCorrection = (code: string): boolean => {
  const type = getInvoiceTypeInfo(code);
  return type?.category === 'Corrected Invoice' || type?.category === 'Substitute';
};

export type CommonInvoiceType = typeof COMMON_INVOICE_TYPES[number];
export type AllInvoiceType = keyof typeof INVOICE_TYPE_DATA;
export type InvoiceTypeCategory = keyof typeof INVOICE_TYPE_CATEGORIES;